/**
 * Privacy Policy — plain-English, comprehensive, honest.
 * Reflects exactly what Aura collects and how it's used (Feb 2026 snapshot).
 * Get a lawyer review before international scale; this is solid for U.S. launch.
 */
export function PrivacyPolicyPage({ onClose }) {
  return (
    <div className="legal-page" data-testid="privacy-page">
      <div className="legal-container">
        <button className="legal-back-btn" onClick={onClose} data-testid="privacy-back-btn">← Back</button>
        <h1>Privacy Policy</h1>
        <p className="legal-meta">Last updated: February 2026</p>

        <p className="legal-intro">
          Aura is an AI companion that learns from you. That means we collect personal data — and we want to be straightforward about exactly what, why, and what you can do about it.
        </p>

        <h2>1. What we collect</h2>
        <ul>
          <li><strong>Account info:</strong> your email, name, hashed password (we never see your plaintext password), and optional profile picture.</li>
          <li><strong>Conversations:</strong> the messages you send Aura and Aura's replies. These are stored so Aura can remember context.</li>
          <li><strong>Mood data:</strong> Aura tracks its own mood state, which is derived from your conversation patterns. We store these mood readings.</li>
          <li><strong>People you mention:</strong> when you talk about friends or family, Aura saves their name and any context you share so it can remember them. You can view, edit, or delete this list at any time in Settings → People Aura Knows.</li>
          <li><strong>Photos & voice clips you choose to send:</strong> these are processed by AI to generate Aura's reaction, then stored on our servers tied to your account.</li>
          <li><strong>Wallpapers:</strong> all wallpapers Aura generates for you are saved in your gallery.</li>
          <li><strong>Payment data:</strong> processed entirely by Stripe. We see only your subscription status, never your card number.</li>
          <li><strong>Connected social accounts (Premium):</strong> if you connect Spotify or Snapchat, we receive what those platforms send us (top artists, etc). We never receive your password.</li>
        </ul>

        <h2>2. What we DON'T collect</h2>
        <ul>
          <li>We don't read your texts, emails, or notifications.</li>
          <li>We don't have access to your contacts, calendar, or browsing history.</li>
          <li>We don't track you across other apps or websites.</li>
          <li>We don't access your photo library unless you explicitly pick a photo to share.</li>
          <li>We don't record audio in the background.</li>
        </ul>

        <h2 data-testid="privacy-logs-heading">3. Yes, we log your conversations — and here's exactly why</h2>
        <p>
          Aura remembers everything you tell her so she can be the friend who actually shows up.
          That means we log your chats, voice transcripts, mood data, and the People you tell her about.
          To be crystal clear about what that does and doesn't mean:
        </p>
        <ul>
          <li><strong>The logs power Aura's memory.</strong> Without them, she'd forget your sister's name, your bad week, your taste in music, the milestone she promised to remember.</li>
          <li><strong>You can see every log.</strong> Settings → Privacy → "Export my data" sends you a download of everything we hold within 30 days. Free, no questions asked.</li>
          <li><strong>You can delete every log.</strong> Settings → Privacy → "Wipe my conversations" purges chat history. Settings → "Delete my account" purges everything we have, permanently, within 24 hours.</li>
          <li><strong>We do NOT sell, share, or rent your conversations.</strong> Not to advertisers, not to data brokers, not to AI training companies, not to anyone. We pay our bills with subscriptions — that's it.</li>
          <li><strong>Your data is private to your account.</strong> Other users never see it. Our own staff only access it if you give us written consent (e.g., debugging a bug you reported).</li>
          <li><strong>Encrypted in transit and at rest.</strong> All traffic is HTTPS. Data is stored on encrypted MongoDB Atlas servers.</li>
        </ul>
        <p style={{ marginTop: "0.5rem" }}>
          <strong>How to find your data on each platform:</strong>
        </p>
        <ul>
          <li><strong>iPhone / iPad (PWA):</strong> Open Aura → tap the menu (⋮) in the top-right → <em>Settings → Privacy</em> → "Export my data" or "Wipe conversations".</li>
          <li><strong>Android (PWA):</strong> Same path — menu (⋮) in the top-right → <em>Settings → Privacy</em>.</li>
          <li><strong>Desktop browser:</strong> Same — top-right menu (⋮) → <em>Settings → Privacy</em>.</li>
          <li><strong>By email:</strong> Mail <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a> with the subject "Export my Aura data" or "Delete my Aura data" — we honor every request within 30 days (sooner if we can).</li>
        </ul>

        <h2>4. How we use your data</h2>
        <ul>
          <li>To make Aura's responses personal and contextual.</li>
          <li>To generate mood-aware wallpapers via OpenAI's image API.</li>
          <li>To remember who matters to you (the People feature).</li>
          <li>To process your subscription via Stripe.</li>
          <li>To send transactional emails (verification, password reset) via SendGrid.</li>
          <li>That's it. We don't sell your data. We don't share it with advertisers. We don't train AI models on it beyond what's necessary to power Aura's replies in your single account.</li>
        </ul>

        <h2>5. Third parties we use</h2>
        <ul>
          <li><strong>OpenAI:</strong> processes your chats, voice clips (Whisper), and image generation (GPT Image 1 / GPT-4o Vision). OpenAI does not retain your conversations for training when accessed via API.</li>
          <li><strong>Stripe:</strong> handles all payments. They have their own <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>.</li>
          <li><strong>SendGrid (Twilio):</strong> sends our transactional emails.</li>
          <li><strong>MongoDB Atlas:</strong> stores your data on encrypted servers.</li>
          <li><strong>Spotify / Snapchat (Premium only, opt-in):</strong> if you connect them.</li>
        </ul>

        <h2>6. Your rights</h2>
        <ul>
          <li><strong>Delete your account:</strong> go to Settings → Delete my account. This permanently erases everything we have about you within 24 hours, including chats, wallpapers, people, mood data, and subscription history.</li>
          <li><strong>Export your data:</strong> email us at <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a> and we'll send you a download of everything we hold within 30 days.</li>
          <li><strong>Cancel subscription:</strong> Settings → Manage subscription. You retain access until the end of the paid period.</li>
          <li><strong>California (CCPA) and EU (GDPR) residents:</strong> you have the right to know what we collect, to delete it, and to object to processing. Email us and we'll honor any valid request within 30 days.</li>
        </ul>

        <h2>7. Children</h2>
        <p>
          Aura is intended for users 13 and older. If you're under 13, please don't use Aura. If we learn we've collected data on a child under 13, we delete it. Parents/guardians of users under 18 can set up parental controls, including emergency contacts who will be notified (with no message content shared — just a gentle heads-up) if Aura detects a serious safety signal.
        </p>

        <h2>8. Changes</h2>
        <p>
          When we change this policy materially, we'll email you at least 14 days before the change takes effect. The "Last updated" date at the top will always reflect the current version.
        </p>

        <h2>9. Contact</h2>
        <p>
          Email us at <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a> for any privacy question, request, or concern.
        </p>
      </div>
    </div>
  );
}
